import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can } from '@gwc/shared';
import { billingApi } from '../api/billing';
import { membersApi } from '../api/members';
import { useAuth } from '../auth/AuthProvider';
import { ApiError } from '../lib/api';

const money = (n: number | string) =>
  Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function InvoiceDetail() {
  const { invoiceId = '' } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const canManage = user ? can(user.roles, 'payments:manage') : false;

  const invoiceQuery = useQuery({
    queryKey: ['billing', 'invoice', invoiceId],
    queryFn: () => billingApi.getInvoice(invoiceId),
    enabled: Boolean(invoiceId),
  });

  const memberQuery = useQuery({
    queryKey: ['members', invoiceQuery.data?.personId],
    queryFn: () => membersApi.get(invoiceQuery.data!.personId),
    enabled: Boolean(invoiceQuery.data?.personId),
  });

  const checkoutMut = useMutation({
    mutationFn: () => billingApi.checkout({ invoiceId, provider: 'stripe' }),
    onSuccess: (result) => {
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
      qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });

  if (invoiceQuery.isLoading) return <p className="text-cream-300/60">Loading invoice…</p>;
  if (invoiceQuery.isError || !invoiceQuery.data) {
    return <p className="text-wine-300">Invoice not found.</p>;
  }

  const inv = invoiceQuery.data;
  const member = memberQuery.data?.member;
  const balance = Number(inv.subtotal) - Number(inv.amountPaid);
  const isPayable = inv.status === 'open' && balance > 0 && user?.id === inv.personId;

  return (
    <div className="mx-auto max-w-2xl space-y-6 print:max-w-none">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link to={canManage ? '/billing' : '/account'} className="text-sm text-cream-300/60 hover:text-gold-200">
          ← Back
        </Link>
        <button type="button" className="btn-ghost text-sm" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <article className="card space-y-6 p-8 print:border-0 print:bg-white print:text-black">
        <header className="border-b border-ink-800 pb-6 print:border-gray-300">
          <p className="text-xs uppercase tracking-[0.3em] text-gold-400 print:text-gray-600">Invoice</p>
          <h1 className="mt-2 font-display text-3xl text-cream-50 print:text-black">{inv.invoiceNumber}</h1>
          <p className="mt-2 text-sm text-cream-300/70 print:text-gray-600">
            Status: <span className="capitalize">{inv.status}</span>
            {inv.issuedAt ? ` · Issued ${inv.issuedAt}` : ''}
            {inv.dueDate ? ` · Due ${inv.dueDate}` : ''}
          </p>
        </header>

        {member && (
          <div>
            <h2 className="text-xs uppercase tracking-wide text-cream-300/50 print:text-gray-500">Bill to</h2>
            <p className="mt-1 text-cream-100 print:text-black">
              {member.firstName} {member.lastName}
            </p>
            <p className="text-sm text-cream-300/60 print:text-gray-600">{member.email}</p>
          </div>
        )}

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink-800 text-left text-cream-300/50 print:border-gray-300 print:text-gray-600">
              <th className="py-2">Description</th>
              <th className="py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.items.map((item) => (
              <tr key={item.id} className="border-b border-ink-800/50 print:border-gray-200">
                <td className="py-3 text-cream-100 print:text-black">{item.label}</td>
                <td className="py-3 text-right text-cream-100 print:text-black">{money(item.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="pt-4 text-right font-display text-cream-300/70 print:text-gray-600">Subtotal</td>
              <td className="pt-4 text-right font-display text-gold-200 print:text-black">{money(inv.subtotal)}</td>
            </tr>
            {Number(inv.amountPaid) > 0 && (
              <tr>
                <td className="text-right text-cream-300/50">Paid</td>
                <td className="text-right text-cream-300/50">−{money(inv.amountPaid)}</td>
              </tr>
            )}
            {balance > 0 && inv.status !== 'paid' && (
              <tr>
                <td className="pt-2 text-right font-display text-cream-50 print:text-black">Balance due</td>
                <td className="pt-2 text-right font-display text-wine-300 print:text-black">{money(balance)}</td>
              </tr>
            )}
          </tfoot>
        </table>

        {inv.notes && <p className="text-sm text-cream-300/60 print:text-gray-600">{inv.notes}</p>}

        {isPayable && (
          <div className="border-t border-ink-800 pt-6 print:hidden">
            <button
              type="button"
              className="btn-primary"
              disabled={checkoutMut.isPending}
              onClick={() => checkoutMut.mutate()}
            >
              {checkoutMut.isPending ? 'Starting checkout…' : `Pay ${money(balance)}`}
            </button>
            {checkoutMut.isSuccess && checkoutMut.data?.message && !checkoutMut.data.checkoutUrl && (
              <p className="mt-3 text-sm text-gold-200">{checkoutMut.data.message}</p>
            )}
            {checkoutMut.isError && (
              <p className="mt-3 text-sm text-wine-300">
                {checkoutMut.error instanceof ApiError ? checkoutMut.error.message : 'Checkout failed.'}
              </p>
            )}
          </div>
        )}
      </article>
    </div>
  );
}
